/**
 * Lightweight signal (no dependencies) that marks a logout as in progress.
 *
 * During logout the `auth.getUser` cache is cleared and refetches as `null`, which would
 * briefly render the logged-out "compare my profile" CTA in the topbar before the client
 * navigation to /login (or the alt-user's page) takes over. Components read this flag to keep
 * showing a neutral skeleton instead of flashing the anonymous CTA.
 *
 * Logout is a client-side navigation (not a full-page reload), so the flag must be reset once
 * the logout has landed — otherwise it would survive into the next session (login is also a
 * client nav) and leave the topbar stuck neutral. `endLogout()` is called when the bare /login
 * page mounts (the topbar is already unmounted there, so resetting causes no flash).
 */
import { useSyncExternalStore } from 'react'

let loggingOut = false
const listeners = new Set<() => void>()

export function beginLogout() {
  if (loggingOut) return
  loggingOut = true
  for (const fn of listeners) fn()
}

export function endLogout() {
  if (!loggingOut) return
  loggingOut = false
  for (const fn of listeners) fn()
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function useIsLoggingOut() {
  return useSyncExternalStore(
    subscribe,
    () => loggingOut,
    () => false,
  )
}
